import { getLuminance, hexToOklch, hexToRgb, oklchToHex } from "./colorUtils"

export type Palette = {
  primary: string
  secondary: string
  tertiary: string
}

export const getContrastRatio = (colorA: string, colorB: string) => {
  const luminanceA = getLuminance(colorA)
  const luminanceB = getLuminance(colorB)

  const brightest = Math.max(luminanceA, luminanceB)
  const darkest = Math.min(luminanceA, luminanceB)

  return (brightest + 0.05) / (darkest + 0.05)
}

// A base at the flip point can reach at most ~4.58:1, so higher targets can be missed silently
const SECONDARY_CONTRAST_TARGET = 2
const TERTIARY_CONTRAST_TARGET = 4

// Above this relative luminance a color has more room to darken than to lighten
const LUMINANCE_FLIP_POINT = 0.179
const LIGHTNESS_STEP = 0.01

const deriveShade = (baseHexColor: string, contrastTarget: number) => {
  const { l, c, h } = hexToOklch(baseHexColor)

  const shouldDarken = getLuminance(baseHexColor) > LUMINANCE_FLIP_POINT
  const lightnessLimit = shouldDarken ? 0 : 1

  let lightness = l
  let shade = baseHexColor

  while (
    getContrastRatio(shade, baseHexColor) < contrastTarget &&
    lightness !== lightnessLimit
  ) {
    lightness = shouldDarken
      ? Math.max(lightness - LIGHTNESS_STEP, lightnessLimit)
      : Math.min(lightness + LIGHTNESS_STEP, lightnessLimit)
    shade = oklchToHex(lightness, c, h)
  }

  return shade
}

export const generatePalette = (baseHexColor: string): Palette => ({
  primary: baseHexColor,
  secondary: deriveShade(baseHexColor, SECONDARY_CONTRAST_TARGET),
  tertiary: deriveShade(baseHexColor, TERTIARY_CONTRAST_TARGET),
})

export const BASE_PALETTE: Palette = generatePalette("#fff")

const darkenColor = (hexColor: string, alpha: number) => {
  const { r, g, b } = hexToRgb(hexColor)

  const darkenChannel = (channel: number) =>
    Math.round(channel * (1 - alpha))
      .toString(16)
      .padStart(2, "0")

  return `#${darkenChannel(r)}${darkenChannel(g)}${darkenChannel(b)}`
}

/**
 * Darken a whole palette towards black, giving the same result as laying a
 * black overlay of the given alpha over it. Alphas outside 0-1 are clamped.
 */
export const darkenPalette = (palette: Palette, alpha: number): Palette => {
  const clampedAlpha = Math.min(Math.max(alpha, 0), 1)

  return {
    primary: darkenColor(palette.primary, clampedAlpha),
    secondary: darkenColor(palette.secondary, clampedAlpha),
    tertiary: darkenColor(palette.tertiary, clampedAlpha),
  }
}

/**
 * Check if the contrast between two colors is accessible within WCAG AA standard (4.5:1)
 */
export const isColorContrastAccessible = (
  textColor: string,
  backgroundColor: string
) => getContrastRatio(textColor, backgroundColor) >= 4.5

export const getAccessibleTextColorForBackgroundColor = (
  backgroundColor: string | undefined
) => {
  if (backgroundColor === undefined) return ""

  if (isColorContrastAccessible(backgroundColor, "#FFFFFF")) return "#FFFFFF" // White
  if (isColorContrastAccessible(backgroundColor, "#4A4A4A")) return "#4A4A4A" // Coal
  if (isColorContrastAccessible(backgroundColor, "#171717")) return "#171717" // Carbon
  return "#000000" // Black
}
